import { createFileRoute } from "@tanstack/react-router";
import { Alert, Button, Descriptions } from "antd";
import { useEffect } from "react";
import { useArtemisAuth } from "@/auth/artemis-auth-provider";
import {
	useDeactivateAccount,
	useMe,
} from "@/common/artemis-operations/members";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	IdField,
	QueryGuard,
} from "@/components/base";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute("/_authenticated/account/")({
	component: AccountSettingsPage,
});

function AccountSettingsPage() {
	const { setTitle } = usePageTitle();
	useEffect(() => setTitle("Account"), [setTitle]);

	const notify = useNotify();
	const { logout } = useArtemisAuth();
	const { data: me, isPending, isError } = useMe({ enabled: true });
	const deactivate = useDeactivateAccount();
	const { confirm, dialog } = useFrictionConfirm();

	return (
		<BasePageContainer>
			<BaseContainer title="Profile">
				<QueryGuard isPending={isPending} isError={isError} isNull={!me}>
					<Descriptions column={1} styles={{ label: { width: 160 } }}>
						<Descriptions.Item label="Name">
							{me?.displayName || "—"}
						</Descriptions.Item>
						<Descriptions.Item label="Email">
							<IdField id={me?.email} />
						</Descriptions.Item>
						<Descriptions.Item label="User ID">
							<IdField id={me?.id} showCopyButton />
						</Descriptions.Item>
						<Descriptions.Item label="Organization ID">
							<IdField id={me?.organizationId} showCopyButton />
						</Descriptions.Item>
					</Descriptions>
				</QueryGuard>
			</BaseContainer>

			<BaseContainer title="Disable account">
				<Alert
					type="warning"
					title="This action cannot be undone"
					description="Disabling your account removes you from your organization. If you are the only administrator of your organization, the organization and all of its data will be deleted as well."
					action={
						<Button
							danger
							loading={deactivate.isPending}
							onClick={() =>
								confirm({
									title: "Disable your account?",
									message:
										"You will be signed out immediately. This cannot be undone.",
									confirmText: "disable",
									confirmButtonLabel: "Disable account",
									onConfirm: () => {
										deactivate.mutateAsync(undefined, {
											onSuccess: () => logout(),
											onError: (err) =>
												notify.error(
													(err as Error).message ||
														"Failed to disable account.",
												),
										});
									},
								})
							}
						>
							Disable account
						</Button>
					}
				/>
			</BaseContainer>
			{dialog}
		</BasePageContainer>
	);
}
